import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import './ServiceUnavailableView.css';

/** Tono de la etiqueta superior; el botón es morado en los tres (contraste AA con texto blanco). */
export type ServiceUnavailableTone = 'warning' | 'info' | 'success';

interface ServiceUnavailableViewProps {
  tone?: ServiceUnavailableTone;
  /** Por omisión recarga el documento: el perfil se cachea en una variable de módulo
   *  (`getCurrentUserProfile`, api/client.ts) y solo una carga completa la vacía. */
  onRetry?: () => void;
}

/**
 * Pantalla «plataforma no disponible» (System Broadcast, 2026-10-16). La muestra `AppRouter` cuando
 * `/v1/users/resolve` termina en 'unavailable' (red, 502/503/504 o tiempo agotado). No consulta
 * ningún servicio: el idioma es el que ya resolvió i18next (preferencia guardada o navegador).
 *
 * No se llama «SystemBroadcast…» a propósito: ese nombre ya lo usa la función de administración que
 * envía avisos de mantenimiento como notificaciones (`SystemBroadcastCard`).
 *
 * El diseño es el de entrega-broadcast/index.html; su copia estática vive en public/broadcast.html
 * (modo mantenimiento de appservice/server.js). Cambiar los dos a la vez.
 */
export function ServiceUnavailableView({ tone = 'warning', onRetry }: Readonly<ServiceUnavailableViewProps>) {
  const { t, i18n } = useTranslation();
  const current = i18n.language?.split('-')[0] ?? 'es';
  const titulo = t('serviceUnavailable.title');

  useEffect(() => {
    document.title = `${titulo} · Sigma`;
  }, [titulo]);

  return (
    <div className="svu" data-testid="service-unavailable">
      <div className="svu-wrap">
        {/* SVG en el documento (y no <img>) para poder girar el brazo con CSS. */}
        <svg className="svu-mascota" viewBox="0 0 378.88 381.37" aria-hidden="true" focusable="false">
          <g className="svu-mascota-grupo">
            <circle className="svu-cabeza" cx="158.67" cy="77.03" r="66.95" />
            <circle className="svu-ojo" cx="143.89" cy="71.29" r="7.92" />
            <circle className="svu-ojo" cx="173.43" cy="71.29" r="7.92" />
            <path className="svu-brazo svu-mult" d="M263.01,350.72c-3.22,0-6.49-.32-9.76-1.02-21.6-4.59-37.03-23.67-37.03-45.75,0-31.66-25.77-57.44-57.44-57.44-25.85,0-46.77-20.95-46.77-46.77s20.95-46.77,46.77-46.77c39.48,0,75.49,15.23,102.41,40.13l21.48-48.17c10.51-23.6,38.18-34.19,61.78-23.67,23.6,10.51,34.19,38.18,23.67,61.78l-62.41,139.97c-7.64,17.13-24.55,27.74-42.7,27.74v-.02Z" />
            <path className="svu-cuerpo svu-mult" d="M165.59,366.06c-2.12,0-4.3-.15-6.47-.45l-110.95-15.33c-23.15-3.2-40.38-22.97-40.38-46.35,0-83.26,67.72-150.98,150.98-150.98,25.85,0,46.77,20.95,46.77,46.77s-20.95,46.77-46.77,46.77c-16.78,0-31.89,7.24-42.4,18.75l55.56,7.69c25.6,3.55,43.48,27.14,39.93,52.74-3.25,23.42-23.3,40.38-46.27,40.38h0Z" />
            <circle className="svu-corazon" cx="158.67" cy="200.09" r="46.47" />
            <path className="svu-corazon-trazo" d="M181.52,180.91c-6.04-6.04-15.83-6.04-21.88,0-.35.35-.67.72-.97,1.1-.3-.37-.62-.75-.97-1.1-6.04-6.04-15.83-6.04-21.88,0s-6.04,15.83,0,21.88l22.9,21.03,22.82-21.03c6.04-6.04,6.04-15.83,0-21.88h-.02Z" />
          </g>
        </svg>

        <main className={`svu-card svu-card--${tone}`} aria-labelledby="svu-titulo">
          <div className="svu-langbar">
            <select
              className="svu-lang"
              value={SUPPORTED_LANGUAGES.some((l) => l.code === current) ? current : 'es'}
              onChange={(e) => { void i18n.changeLanguage(e.target.value); }}
              aria-label={t('serviceUnavailable.changeLanguage')}
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.code.toUpperCase()}</option>
              ))}
            </select>
          </div>

          <span className="svu-etiqueta">{t('serviceUnavailable.label')}</span>
          <h1 id="svu-titulo" className="svu-titulo">{titulo}</h1>
          <p className="svu-mensaje">{t('serviceUnavailable.message')}</p>

          <div className="svu-acciones">
            <button type="button" className="svu-btn" onClick={onRetry ?? (() => window.location.reload())}>
              {t('serviceUnavailable.retry')}
            </button>
          </div>

          <div className="svu-franja" aria-hidden="true">
            <span /><span /><span /><span />
          </div>
        </main>
      </div>
    </div>
  );
}

export default ServiceUnavailableView;
